import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { logChatbotLoadChatbot } from '../../bi';
import { useFedopsLogger } from '../../common/providers/fedopsLogger';
import { selectConversationId } from '../../common/selectors/chat';
import { selectEditorType } from '../../common/selectors/homepage';
import { selectIsCopilotInitialized, selectLoadPageUrl } from '../../common/selectors/meta';
import { selectWidgetSessionId } from '../../common/selectors/widgetSession';
import { loadChatbotStartChat } from '../../common/thunks/chatbotStartChat.thunk';
import { widgetSDK } from '../../sdk/widgetSDK';
import { addWixRecorder } from '../../util/addWixRecorder';
import { renderModel } from '../../util/renderModel';

const isCopilot = renderModel.isCopilot();

const useAppLoaded = () => {
  const fedopsLogger = useFedopsLogger();
  const store = useStore();

  return useCallback(() => {
    logChatbotLoadChatbot(store.getState());
    fedopsLogger.appLoaded();
    const sessionId = selectWidgetSessionId(store.getState());
    const conversationId = selectConversationId(store.getState());
    addWixRecorder({ sessionId, conversationId });
  }, [fedopsLogger, store]);
};

export const useLoadApp = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const appLoaded = useAppLoaded();
  const isCopilotInitialized = useSelector(selectIsCopilotInitialized);
  const loadPageUrl = useSelector(selectLoadPageUrl);
  const editorTypeFromRedux = useSelector(selectEditorType);

  const fedopsLogger = useFedopsLogger();
  useEffect(() => {
    if (isCopilot && !isCopilotInitialized) {
      return;
    }
    const loadApp = async () => {
      const context = await widgetSDK.getContext(isCopilot ? loadPageUrl : undefined);
      const editorType = selectEditorType(store.getState());
      await dispatch(loadChatbotStartChat({ context, editorType }));
      appLoaded();
    };
    fedopsLogger.appLoadStarted();
    void loadApp();
  }, [appLoaded, dispatch, fedopsLogger, isCopilotInitialized, loadPageUrl, store]);

  const prevEditorType = useRef<string | undefined>(editorTypeFromRedux);
  useEffect(() => {
    if (editorTypeFromRedux !== undefined && editorTypeFromRedux !== prevEditorType.current) {
      prevEditorType.current = editorTypeFromRedux;
      const reload = async () => {
        const context = await widgetSDK.getContext(isCopilot ? loadPageUrl : undefined);
        await dispatch(loadChatbotStartChat({ context, editorType: editorTypeFromRedux }));
      };
      void reload();
    }
  }, [editorTypeFromRedux, dispatch, loadPageUrl]);
};
